import { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { formatCurrency } from '@/utils/formatCurrency';
import {
  CANONICAL_STAGES,
  CANONICAL_STAGE_LABELS,
  deriveFlag,
  deriveUrgency,
  getDaysInMilestone,
  getDaysSinceLastActivity,
  getDaysStale,
  mapToCanonicalStage,
  type CanonicalStage,
  type RiskFlag as RiskFlagType,
  type Urgency
} from '../../activeLoanModel';
import type { ProspectLoan } from '../../types';
import { InitialsAvatar } from '../primitives/InitialsAvatar';
import { ChannelTag } from '../primitives/ChannelTag';
import { LockStatusPill } from '../primitives/LockStatusPill';
import { RiskFlag } from '../primitives/RiskFlag';
import { ActivityChip } from '../primitives/ActivityChip';
import { ConversationLogHoverCard } from '../primitives/ConversationLogHoverCard';
import { LoanNumberCell } from '../primitives/LoanNumberCell';

interface PipelineDirectionProps {
  prospects: ProspectLoan[];
  /**
   * Full-fetch loading state. Pipeline column distributions are incorrect until
   * the whole segmented set is loaded, so we must not render partial columns
   * mid-loop (per the approved plan).
   */
  isLoading: boolean;
  onViewDetails: (prospect: ProspectLoan) => void;
  /**
   * Stages per row. Stages wrap onto more rows as the view narrows instead of
   * scrolling sideways.
   */
  columns?: 6 | 3 | 2 | 1;
}

const COLUMNS_CLASS: Record<NonNullable<PipelineDirectionProps['columns']>, string> = {
  6: 'grid-cols-6',
  3: 'grid-cols-3',
  2: 'grid-cols-2',
  1: 'grid-cols-1'
};

const FLAG_BORDER: Record<RiskFlagType, string> = {
  stalled: 'border-l-[hsl(38_92%_50%)]'
};

const URGENCY_TEXT: Record<Urgency, string> = {
  ok: 'text-muted-foreground',
  warn: 'text-warning font-medium',
  urgent: 'text-destructive font-semibold'
};

export function PipelineDirection({ prospects, isLoading, onViewDetails, columns = 6 }: PipelineDirectionProps) {
  const byStage = useMemo(() => {
    const map = new Map<CanonicalStage, ProspectLoan[]>();
    for (const stage of CANONICAL_STAGES) {
map.set(stage, []);
}
    for (const loan of prospects) {
      const stage = mapToCanonicalStage(loan);
      map.get(stage)?.push(loan);
    }
    return map;
  }, [prospects]);

  // CRITICAL: while the full fetch is in progress, distributions are incomplete.
  // Render a skeleton rather than partial columns.
  if (isLoading) {
    return (
      <div className={cn('grid gap-2.5 pb-5', COLUMNS_CLASS[columns])}>
        {CANONICAL_STAGES.map(stage => (
          <div key={stage} className="rounded-lg border bg-muted/40 p-2.5 min-h-[240px]">
            <div className="h-4 w-20 animate-pulse rounded bg-muted" />
            <div className="mt-3 space-y-2">
              <div className="h-16 animate-pulse rounded bg-background" />
              <div className="h-16 animate-pulse rounded bg-background" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className={cn('grid gap-2.5 pb-5', COLUMNS_CLASS[columns])}>
      {CANONICAL_STAGES.map(stage => {
        const loans = byStage.get(stage) ?? [];
        const total = loans.reduce((s, l) => s + (l.trans_details_loan_amt ?? 0), 0);
        return (
          <div
            key={stage}
            role="group"
            aria-label={`${CANONICAL_STAGE_LABELS[stage]} stage`}
            className="rounded-lg border bg-muted/40 p-2.5 flex flex-col min-w-0"
          >
            <div className="flex items-center justify-between mb-2 px-0.5">
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-semibold uppercase tracking-wider text-foreground">
                  {CANONICAL_STAGE_LABELS[stage]}
                </span>
              </div>
              <span className="inline-flex min-w-[22px] items-center justify-center rounded-full border bg-background px-1.5 py-0.5 text-[10px] font-mono text-muted-foreground">
                {loans.length}
              </span>
            </div>
            <div className="text-[10px] font-mono text-muted-foreground mb-2">
              {formatCurrency(total)}
            </div>
            {/* Side-by-side stages each scroll within the screen height. Once
                stages wrap to one or two per row the page scrolls instead. */}
            <div
              className={cn('flex flex-col gap-1.5', columns >= 3 && 'overflow-y-auto')}
              style={columns >= 3 ? { maxHeight: 'calc(100vh - 330px)' } : undefined}
            >
              {loans.length === 0 ? (
                <div className="text-[11px] text-muted-foreground italic py-2">Empty</div>
              ) : (
                loans.map(loan => <PipelineCard key={loan.id} loan={loan} onOpen={onViewDetails} />)
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

interface PipelineCardProps {
  loan: ProspectLoan;
  onOpen: (l: ProspectLoan) => void;
}

function PipelineCard({ loan, onOpen }: PipelineCardProps) {
  const flag = deriveFlag(loan);
  const days = getDaysInMilestone(loan);
  const daysStale = getDaysStale(loan);
  const daysSinceActivity = getDaysSinceLastActivity(loan);
  const hasConversationLogs = (loan.conversation_logs?.length ?? 0) > 0;
  const urgency = deriveUrgency(days);
  const borrower = `${loan.borrower_first_name ?? ''} ${loan.borrower_last_name ?? ''}`.trim();
  const daysLabel = days === null
    ? null
    : days === 0 ? 'Today' : days === 1 ? '1 day' : `${days} days`;
  const purpose = loan.trans_details_loan_purpose;
  // Synced verbatim from Encompass CX.LOAN.STATUS (same source as the Table
  // view column); blank sync values count as missing.
  const loanStatus = loan.cx_loan_status?.trim() || null;

  // The borrower name is the card's one primary control. Its ::after layer
  // covers the card so the whole surface opens the loan, without nesting the
  // copy and notes buttons inside another interactive element.
  return (
    <article
      className={cn(
        'group relative flex flex-col gap-1.5 rounded-md border bg-background p-2.5 text-left transition hover:border-muted-foreground/30 hover:shadow-sm hover:-translate-y-px',
        flag && 'border-l-[3px]',
        flag && FLAG_BORDER[flag]
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-1.5 min-w-0">
          <InitialsAvatar name={borrower} size={22} />
          <button
            type="button"
            onClick={() => onOpen(loan)}
            aria-label={borrower ? undefined : `Open loan ${loan.trans_details_loan_number ?? ''}`.trim()}
            className="min-w-0 rounded-sm text-left text-[12px] font-medium text-foreground after:absolute after:inset-0 after:rounded-md after:content-[''] focus-visible:outline-none focus-visible:after:ring-2 focus-visible:after:ring-primary/40 focus-visible:after:ring-offset-1"
          >
            <span className="block truncate">{borrower || '—'}</span>
          </button>
        </div>
        <span className="relative z-10 shrink-0">
          <LoanNumberCell
            loanNumber={loan.trans_details_loan_number}
            className="text-[10px]"
          />
        </span>
      </div>
      <div
        className={cn('whitespace-normal break-words leading-snug text-[11px]', loanStatus ? 'text-foreground' : 'text-muted-foreground')}
        data-testid="pipeline-card-loan-status"
      >
        {loanStatus ?? '—'}
      </div>
      <div className="font-mono text-[14px] font-semibold tracking-tight text-foreground">
        {formatCurrency(loan.trans_details_loan_amt)}
      </div>
      <div className="flex items-center gap-2 text-[11px] text-muted-foreground min-w-0">
        <ChannelTag channel={loan.channel} />
        {purpose && (
          <>
            <span aria-hidden className="inline-block h-[3px] w-[3px] rounded-full bg-muted-foreground/50" />
            <span className="truncate">{purpose}</span>
          </>
        )}
      </div>
      {flag === 'stalled' ? (
        <ConversationLogHoverCard loan={loan} variant="stalled" days={daysStale} className="relative z-10 self-start">
          <RiskFlag flag="stalled" daysStale={daysStale} />
        </ConversationLogHoverCard>
      ) : hasConversationLogs ? (
        <ConversationLogHoverCard loan={loan} variant="active" days={daysSinceActivity} className="relative z-10 self-start">
          <ActivityChip days={daysSinceActivity} />
        </ConversationLogHoverCard>
      ) : null}
      <div className="flex items-center justify-between border-t border-dashed pt-1.5 mt-0.5">
        <span className={cn('text-[10px] font-mono', URGENCY_TEXT[urgency])}>
          {daysLabel ?? ''}
        </span>
        <LockStatusPill loan={loan} />
      </div>
    </article>
  );
}
